import { compact } from 'lodash'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLingui } from '@lingui/react'
import { t } from '@lingui/macro'
import { skipToken } from '@reduxjs/toolkit/query'

import AssignmentIcon from '@mui/icons-material/Assignment'
import EmailIcon from '@mui/icons-material/Email'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

import ConsultationDocumentsIcon from '@/components/ui/icons/ConsultationDocuments'
import PatientIdentityIcon from '@/components/ui/icons/PatientIdentity'
import VitalParametersIcon from '@/components/ui/icons/VitalParameters'
import { Room } from '@/core/models/room.model'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useMedicalActsLicenseEnabled } from '@/features/medical-acts/model/useMedicalActsLicenseEnabled'
import { useShowDMP } from '@/features/dmp-connect'

export enum EmergencySidebarMenuItem {
  PATIENT_INFO = 'patientInfo',
  MESURES = 'mesures',
  DOCUMENTS = 'documents',
  FORMS = 'forms',
  MEDICAL_ACTS = 'medicalActs',
  DMP = 'dmp',
  MSS = 'mss',
}

export function useEmergencySidebarMenu(emergencyRoom?: Room | null) {
  const lingui = useLingui()
  const i18next = useTranslation()

  const hasMedicalActsLicense = useMedicalActsLicenseEnabled()
  const { data: emergencyConsultation } = useGetConsultationQuery(emergencyRoom?.currentConsultation ?? skipToken)
  const showDMP = useShowDMP(emergencyConsultation?.patient._id)

  const sidebarMenuItems = useMemo(
    () =>
      compact([
        {
          id: EmergencySidebarMenuItem.PATIENT_INFO,
          icon: PatientIdentityIcon,
          label: i18next.t('page.liveConsultation.sidebar.patientInfo.label'),
        },
        {
          id: EmergencySidebarMenuItem.MESURES,
          icon: VitalParametersIcon,
          label: i18next.t('page.liveConsultation.sidebar.mesures.label'),
        },
        {
          id: EmergencySidebarMenuItem.DOCUMENTS,
          icon: ConsultationDocumentsIcon,
          label: i18next.t('page.liveConsultation.sidebar.documents.label'),
        },
        {
          id: EmergencySidebarMenuItem.FORMS,
          icon: AssignmentIcon,
          label: i18next.t('page.localConsultation.sidebar.forms.label'),
        },
        hasMedicalActsLicense && {
          id: EmergencySidebarMenuItem.MEDICAL_ACTS,
          icon: LocalHospitalIcon,
          label: i18next.t('page.emergency.sidebar.medicalActs.label', { defaultValue: 'Medical Acts' }),
        },
        showDMP && {
          id: EmergencySidebarMenuItem.DMP,
          icon: FolderSharedIcon,
          label: lingui._(t`DMP`),
        },
        showDMP && {
          id: EmergencySidebarMenuItem.MSS,
          icon: EmailIcon,
          label: 'MS Santé',
        },
      ]),
    [i18next.t, hasMedicalActsLicense, lingui, showDMP]
  )

  const [selectedSidebarMenuItem, setSelectedSidebarMenuItem] = useState<EmergencySidebarMenuItem>(EmergencySidebarMenuItem.PATIENT_INFO)

  return { sidebarMenuItems, selectedSidebarMenuItem, setSelectedSidebarMenuItem }
}

export type UseEmergencySidebarMenuReturn = ReturnType<typeof useEmergencySidebarMenu>
